import type { Transaction } from '../types';
import { formatSaleDate, formatUsd, platformMeta } from '../utils/market';

interface TransactionItemProps {
  transaction: Transaction;
}

export default function TransactionItem({ transaction }: TransactionItemProps) {
  const market = platformMeta(transaction.platform);
  const hasUsdPrice = typeof transaction.priceUsd === 'number' && Number.isFinite(transaction.priceUsd);
  const soldDateTime = Number.isFinite(transaction.soldTime) ? new Date(transaction.soldTime).toISOString() : undefined;

  return (
    <article className="rounded-[20px] border border-border bg-white p-3.5 transition-colors hover:border-cyan/35 sm:p-4">
      <div className="grid grid-cols-[76px_minmax(0,1fr)] gap-3.5 sm:grid-cols-[92px_minmax(0,1fr)] sm:gap-5">
        <div className="aspect-[63/88] overflow-hidden rounded-xl border border-border bg-background-secondary">
          {transaction.soldImage ? (
            <img src={transaction.soldImage} alt={transaction.description || 'Sold card listing'} className="h-full w-full object-cover" loading="lazy" />
          ) : (
            <div className="flex h-full items-center justify-center text-secondary-light">
              <svg className="h-7 w-7" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.6-4.6a2 2 0 0 1 2.8 0L16 16m-2-2 1.6-1.6a2 2 0 0 1 2.8 0L20 14M6 20h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2Z" />
              </svg>
            </div>
          )}
        </div>

        <div className="flex min-w-0 flex-col">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-full px-2.5 py-1 text-[9px] font-extrabold uppercase tracking-[0.08em]" style={{ color: market.color, backgroundColor: market.softColor }}>
              {market.name}
            </span>
            <time className="text-[10px] font-medium text-secondary-light" dateTime={soldDateTime}>
              {formatSaleDate(transaction.soldTime)}
            </time>
          </div>

          <h3 className="mt-2 line-clamp-2 text-sm font-semibold leading-5 text-primary sm:text-[15px]">{transaction.description || 'Untitled sold listing'}</h3>

          <div className="mt-auto flex flex-wrap items-end justify-between gap-3 pt-4">
            <div>
              <span className="block text-[9px] font-bold uppercase tracking-[0.1em] text-secondary-light">Sold price · USD</span>
              <span className={`price-numeral mt-1 block text-xl font-bold tracking-[-0.035em] ${hasUsdPrice ? 'text-primary' : 'text-secondary-light'}`}>
                {formatUsd(transaction.priceUsd)}
              </span>
            </div>
            <div className="text-right">
              {transaction.originalCardInfo?.cardNumber && (
                <span className="block font-mono text-[10px] font-semibold text-secondary">{transaction.originalCardInfo.cardNumber}</span>
              )}
              {transaction.originalCardInfo?.cardIdOnPlatform && (
                <span className="mt-1 block max-w-[140px] truncate font-mono text-[9px] text-secondary-light" title={transaction.originalCardInfo.cardIdOnPlatform}>
                  Market ID · {transaction.originalCardInfo.cardIdOnPlatform}
                </span>
              )}
              <span className="mt-1 block text-[9px] text-secondary-light">FX date · {transaction.fxAsOf || 'Unavailable'}</span>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}
